/**
 * Level-up rewards: FWP.check_new_level (FWP.ts check_new_level) and the FWP.end_game object it fills.
 *
 * check_new_level is called by end_game_fn after a won battle and by the achievement / task bonus claims
 * (new_interf 'get_achiev_bonus' / 'get_task_bonus'): those ops can reuse checkNewLevel with newEndGame().
 */
import { int, keys } from '../as3';
import type { RuleContext } from '../context';
import { playerLevel } from '../defs';
import type { PlayerState } from '../state';
import { perkEffect } from './battle-formulas';

/** A grave the battle left: an enemy worm ('G' object with wteam != 1) - input of the grave search. */
export interface BattleGrave {
  name: string;
  cpu_level: number;
}

/**
 * FWP.end_game exactly as end_game_fn (and the bonus claims) build it: the win / new level / surprise screens and the
 * grave search read it. `surprise_weapons` and `graves` are AS3 counted arrays: [n, item 1, ..., item n] (the
 * initial [0, ""] / [0, {}] placeholders included).
 */
export interface EndGame {
  win: boolean;
  exp_bonus: number;
  gold_bonus: number;
  rating_bonus: number;
  levels_upg: number;
  diam_bonus: number;
  skill_pts: number;
  perk_pts: number;
  surprise_weapons: any[];
  game_stage: number;
  game_mode: string;
  graves: any[];
}

/** The end_game object of a claim (new_interf 'get_achiev_bonus' / 'get_task_bonus'): no battle, nothing won yet. */
export function newEndGame(): EndGame {
  return {
    win: false,
    exp_bonus: 0,
    gold_bonus: 0,
    rating_bonus: 0,
    levels_upg: 0,
    diam_bonus: 0,
    skill_pts: 0,
    perk_pts: 0,
    surprise_weapons: [0, ''],
    game_stage: 0,
    game_mode: '',
    graves: [0, {}],
  };
}

/** Rating per level gained. */
export const RATING_PER_LEVEL = 100;
/** Skill points ("очки способностей") per level before the legioner perk. */
export const SKILL_PTS_PER_LEVEL = 15;
/** Perk points ("очки навыков") per level before the education perk. */
export const PERK_PTS_PER_LEVEL = 20;
/** Diamonds for every ten levels passed. */
export const DIAM_PER_TEN_LEVELS = 10;

/**
 * FWP.check_new_level: when the level derived from exp differs from player.level by delta, grants
 *   rating += 100 * delta, skill_pts += (15 + legioner) * delta, perk_pts += (20 + education) * delta,
 *   tasks 3 -> 4 and 9 -> 10, diam += 10 per tens digit passed, and with the surprise perk one weapon per level
 *   (ROUL_VER surprise_<perk level> class odds, then random weapon ids; draws: int(0, 99), then int(1, 200) until a
 *   weapon of that class matches - the same order and ranges as the original).
 * Fills `endGame` (levels_upg, skill_pts, perk_pts, diam_bonus, surprise_weapons) like the original.
 * `perks` (default player.perks) are the perks it reads: a battle passes those of its start (BattleRewardBasis).
 *
 * PORT fix (ops.txt level.up, design D2 "stale level"): the original never updates player.level here - only
 * new_interf.init_new_level (after the 'battle_end' save) and net_initialized do - so the saved state kept a stale
 * level, and a rule that ran check_new_level again on it would grant the same levels twice. The rule sets
 * player.level to the level derived from exp, the value init_new_level assigns right afterwards.
 */
export function checkNewLevel(p: PlayerState, endGame: EndGame, ctx: Pick<RuleContext, 'defs' | 'rng'>, perks: Record<string, number> = p.perks): void {
  const defs = ctx.defs;
  let delta = 0;
  const newLevel = playerLevel(defs, p.exp);
  const oldLevel = p.level;
  if (oldLevel != newLevel) {
    delta = newLevel - oldLevel;
    p.rating += RATING_PER_LEVEL * delta;
    endGame.levels_upg = delta;
    let education = 0;
    if (perks['education']) education = perkEffect('education', perks.education, '');
    let legioner = 0;
    if (perks['legioner']) legioner = perkEffect('legioner', perks.legioner, '');
    endGame.skill_pts = (SKILL_PTS_PER_LEVEL + legioner) * delta;
    endGame.perk_pts = (PERK_PTS_PER_LEVEL + education) * delta;
    p.skill_pts = int(p.skill_pts + endGame.skill_pts);
    p.perk_pts = int(p.perk_pts + endGame.perk_pts);
    if (p.cur_task == 3) p.cur_task = 4;
    if (p.cur_task == 9) p.cur_task = 10;
    if (Math.floor(newLevel / 10) != Math.floor(oldLevel / 10)) {
      endGame.diam_bonus = (Math.floor(newLevel / 10) - Math.floor(oldLevel / 10)) * DIAM_PER_TEN_LEVELS;
      p.diam = int(p.diam + endGame.diam_bonus);
    }
    if (perks['surprise']) {
      for (let n = 1; n <= delta; n++) surpriseWeapon(p, perks['surprise'], endGame, n, ctx);
    }
  }
  p.level = int(newLevel);
}

/** Weapon ids the surprise perk draws from (true_random(1, 200)). */
export const SURPRISE_WEAPON_ID_MAX = 200;
/** WEAPONS_DEF id the surprise perk never gives (61). */
export const SURPRISE_EXCLUDED_ID = 61;

/** The n-th surprise weapon of one check_new_level (the body of the original's `while (_loc7_ <= _loc2_)` loop). */
function surpriseWeapon(p: PlayerState, surprise: number, endGame: EndGame, n: number, ctx: Pick<RuleContext, 'defs' | 'rng'>): void {
  const W = ctx.defs.WEAPONS_DEF;
  let pick = '';
  const odds = ctx.defs.ROUL_VER['surprise_' + surprise];
  // new Array(odds[0], 0): cumulative odds, cum[0] = odds[0] (0 in every surprise table).
  const cum: number[] = [odds[0], 0];
  for (let i = 1; i <= 7; i++) cum[i] = odds[i] + cum[i - 1];
  let cls = 0;
  const r = ctx.rng.int(0, 99);
  for (let j = 0; j <= 7; j++) {
    if (r < cum[j]) {
      cls = j;
      break;
    }
  }
  if (cls != 0) {
    const matches = (k: string) =>
      W[k].id >= 1 && W[k].id <= SURPRISE_WEAPON_ID_MAX && W[k].id != SURPRISE_EXCLUDED_ID && k.search('rez') == -1 && W[k].wpclass == cls && endGame.surprise_weapons[n - 1] != k;
    // Guard (not in the original): the do-while below never ends when no weapon can match. That cannot happen with the
    // shipped tables (every class the surprise odds can pick has several weapons, tests/rules/battle-formulas.test.ts),
    // but a server must not hang on a broken table.
    if (!keys(W).some((k) => matches(String(k)))) throw new Error(`check_new_level: no surprise weapon of class ${cls}`);
    do {
      const id = ctx.rng.int(1, SURPRISE_WEAPON_ID_MAX);
      for (const key of keys(W)) {
        const k = String(key);
        if (W[k].id == id && W[k].id != SURPRISE_EXCLUDED_ID && k.search('rez') == -1) {
          if (W[k].wpclass == cls && endGame.surprise_weapons[n - 1] != k) pick = k;
        }
      }
    } while (pick == '');
    if (Boolean(p.weapons[pick]) && p.weapons[pick][0] < W[pick].limit) {
      ++p.weapons[pick][0];
      p.weapons[pick][p.weapons[pick][0]] = { range: 1, stolen: false, crafted: false };
    } else if (!p.weapons[pick]) {
      p.weapons[pick] = [1, { range: 1, stolen: false, crafted: false }];
    }
    endGame.surprise_weapons[n] = pick;
    ++endGame.surprise_weapons[0];
  }
}
